import { generateChatCompletion, GenerationCost } from '../openai-chat';
import { CompetitorData, KeywordData, ProjectContext, ArticleOutline } from './types';
import { buildOutlinePrompt } from './prompts';

export async function generateOutline(
  competitors: CompetitorData[],
  keywords: KeywordData,
  projectContext: ProjectContext,
  language?: string,
  contentType?: string,
  intent?: string,
  extractedData?: string,
  customPrompt?: string
): Promise<{ outline: ArticleOutline; cost: GenerationCost }> {
  const prompt = customPrompt || buildOutlinePrompt(competitors, keywords, projectContext, language, contentType, intent, extractedData);

  console.log('� [STAGE 1 - OUTLINE GENERATOR] PROMPT:');
  console.log('═'.repeat(80));
  console.log(prompt);
  console.log('═'.repeat(80));

  console.log('�🔵 [Outline Generator] Sending request to AI...');
  
  const { content: response, cost } = await generateChatCompletion([
    {
      role: 'system',
      content: 'You are a JSON generator. You MUST respond with ONLY valid JSON, no explanations, no markdown, no text before or after the JSON object.'
    },
    {
      role: 'user',
      content: prompt
    }
  ]);

  console.log('🔵 [Outline Generator] Raw AI response length:', response.length);
  console.log('💰 [Outline Generator] Cost:', cost);
  console.log('🔵 [Outline Generator] First 200 chars:', response.substring(0, 200));

  // Parse JSON from response
  // First, decode HTML entities
  let cleanResponse = response
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

  // Remove HTML tags
  cleanResponse = cleanResponse
    .replace(/<pre><code[^>]*>/gi, '')
    .replace(/<\/code><\/pre>/gi, '')
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, ''); // Remove any remaining HTML tags

  // Remove markdown code blocks
  cleanResponse = cleanResponse
    .replace(/```json\n?/gi, '')
    .replace(/```\n?/g, '')
    .replace(/`/g, '')
    .trim();

  console.log('🔵 [Outline Generator] After HTML cleanup, length:', cleanResponse.length);

  // Try to find JSON object - match from first { to last }
  let jsonStr = cleanResponse;
  const firstBrace = cleanResponse.indexOf('{');
  const lastBrace = cleanResponse.lastIndexOf('}');
  
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    jsonStr = cleanResponse.substring(firstBrace, lastBrace + 1);
  }

  console.log('🔵 [Outline Generator] Extracted JSON, length:', jsonStr.length);
  console.log('🔵 [Outline Generator] JSON preview:', jsonStr.substring(0, 300));

  try {
    const outline = JSON.parse(jsonStr) as ArticleOutline;
    
    console.log('✅ [Outline Generator] JSON parsed successfully');
    console.log('✅ [Outline Generator] H1:', outline.h1);
    console.log('✅ [Outline Generator] Sections count:', outline.sections?.length);
    
    // Validate outline structure
    if (!outline.h1 || !outline.sections || outline.sections.length === 0) {
      throw new Error('Invalid outline structure: missing h1 or sections');
    }

    // Validate sections have required fields
    for (let i = 0; i < outline.sections.length; i++) {
      const section = outline.sections[i];
      if (!section.h2 || !section.targetKeywords) {
        throw new Error(`Invalid section ${i}: missing h2 or targetKeywords`);
      }
      if (!section.targetKeywords.primary) {
        throw new Error(`Invalid section ${i}: missing primary keyword`);
      }
    }

    console.log('✅ [Outline Generator] Validation passed');
    
    // Додаємо toggles зі значеннями за замовчуванням
    outline.includeIntroduction = outline.includeIntroduction !== undefined ? outline.includeIntroduction : true;
    outline.includeConclusion = outline.includeConclusion !== undefined ? outline.includeConclusion : true;
    outline.includeCta = outline.includeCta !== undefined ? outline.includeCta : true;
    outline.includeFaq = outline.includeFaq !== undefined ? outline.includeFaq : true; // FAQ за замовчуванням включено
    
    return { outline, cost };
    
  } catch (error) {
    console.error('❌ [Outline Generator] Failed to parse JSON:', error);
    console.error('❌ [Outline Generator] Original response:', response);
    console.error('❌ [Outline Generator] Cleaned response:', cleanResponse);
    console.error('❌ [Outline Generator] Extracted JSON:', jsonStr);
    
    // Try to give helpful error message
    if (jsonStr.length === 0) {
      throw new Error('AI response contained no JSON object. The AI may have returned only text.');
    }
    
    throw new Error('Failed to generate valid outline: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}
